import { useMemo } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import {
  LinkFragment as HultaforsgroupLinkFragment,
  PageNavigationBlockFragment as HultaforsgroupPageNavigationBlockFragment,
} from '@hultafors/hultaforsgroup/types';
import {
  LinkFragment as SnickersLinkFragment,
  PageNavigationFragment as SnickersPageNavigationBlockFragment,
} from '@hultafors/snickers/types';

import ChevronSvg from '../../svg/chevron.svg';
import { SharedLink } from '../link/link';

import styles from './page-navigation-block.module.scss';

import type { AccordionItemClassNames } from '../accordion-item/accordion-item';

const Accordion = dynamic(() =>
  import('../accordion/accordion').then((mod) => mod.Accordion),
);
const AccordionItem = dynamic(() =>
  import('../accordion-item/accordion-item').then((mod) => mod.AccordionItem),
);

type LinkFragment = HultaforsgroupLinkFragment | SnickersLinkFragment;
type PageNavigationBlockProps = (
  | HultaforsgroupPageNavigationBlockFragment
  | SnickersPageNavigationBlockFragment
) & {
  activeIndex?: number;
  className?: string;
};

export const PageNavigationBlock: React.FC<PageNavigationBlockProps> = ({
  id,
  label,
  links,
  activeIndex: initialActiveIndex,
  className,
}) => {
  const { asPath } = useRouter();
  const activeIndex = useMemo(() => {
    const currentIndex = links.findIndex(
      ({ url }) => url && asPath.startsWith(url),
    );
    const currentLink = links[currentIndex];
    if (currentLink?.url && asPath.startsWith(currentLink.url)) {
      return currentIndex;
    }
    if (
      typeof initialActiveIndex === 'number' &&
      initialActiveIndex >= 0 &&
      initialActiveIndex < links.length
    ) {
      return initialActiveIndex;
    }
  }, [initialActiveIndex, links, asPath]);

  const linkMapper = (link: LinkFragment, index: number) => {
    if (!link.url) {
      return null;
    }
    const active = activeIndex === index;
    return (
      <SharedLink
        key={`PageNavigationLink-${id}-${link.id}`}
        href={link.url}
        className={clsx(styles['link'], active && styles['link--active'])}
      >
        {link.title ?? link.url}
      </SharedLink>
    );
  };

  if (!links || links.length === 0) {
    return null;
  }

  const accordionItemClassNames: AccordionItemClassNames = {
    content: styles['accordion-content'],
    item: styles['accordion-item'],
    trigger: clsx(styles['accordion-trigger'], styles['link']),
  };

  return (
    <nav className={clsx(styles['page-navigation'], className)}>
      <ul className={styles['landscape-navigation']}>
        {links.map((link, i) => (
          <li key={`PageNavigationListItem-${id}-${link.id}`}>
            {linkMapper(link, i)}
          </li>
        ))}
      </ul>
      <div className={styles['portrait-navigation']}>
        <Accordion>
          <AccordionItem
            label={label}
            value={`accordion-${label}-${id}`}
            indicator={<ChevronSvg aria-hidden={true} focusable={false} />}
            classNames={accordionItemClassNames}
          >
            {links.map(linkMapper)}
          </AccordionItem>
        </Accordion>
      </div>
    </nav>
  );
};
